import type { Progress, ProgressRepo } from '@dcs/platform/ports';
import {
  ArrowRightIcon,
  BookIcon,
  ClipboardIcon,
  SteeringWheelIcon,
  WarningIcon,
} from '@dcs/ui/icons';
import { Card, ProgressBar, Segmented } from '@dcs/ui/primitives';
import { useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { Link } from 'react-router';
import counts, { openExercises } from 'virtual:theory-counts';
import { slugOf, useHomeClass, HOME_CLASSES, type HomeClassStore } from './class-store';
import {
  continueAction,
  theoryProgress,
  type ContinueAction,
  type LatestAttempt,
} from './continue';
import {
  useLatestAttempt,
  type LatestAttemptQuery,
  type LoadLatestAttemptApi,
} from './latest-attempt';
import { REGULATION_FULL, homeFns, ht } from './messages';
import { currentRegulation } from './regulation';
import './home.css';

export type HomeAuthStatus = 'loading' | 'anonymous' | 'authenticated';

export interface HomeScreenProps {
  readonly authStatus: HomeAuthStatus;
  /** The signed-in user's id; `null` for a guest or while the session loads. */
  readonly userId: string | null;
  readonly classStore: HomeClassStore;
  readonly progressRepo: ProgressRepo;
  readonly loadApi: LoadLatestAttemptApi;
  /** `yyyy-mm-dd` of today: picks the current regulation set for the chip. */
  readonly today: string;
  /** The page's `<h1>`, visually hidden (the app's name; no greeting). */
  readonly title: string;
  /** A notice above the page (the account was deleted). */
  readonly notice?: ReactNode;
}

const CLASS_OPTIONS = HOME_CLASSES.map((value) => ({ value, label: value }));

/**
 * Progress of one class as an external store: the snapshot is cached per class and replaced when
 * the repo reports a change, so React sees a stable value between changes.
 */
function useClassProgress(repo: ProgressRepo, licenseClass: string): Progress {
  const source = useMemo(() => {
    const cache = new Map<string, Progress>();
    return {
      subscribe: (onChange: () => void) =>
        repo.subscribe((changed, progress) => {
          cache.set(changed, progress);
          onChange();
        }),
      get: (key: string) => {
        let progress = cache.get(key);
        if (progress === undefined) {
          progress = repo.load(key);
          cache.set(key, progress);
        }
        return progress;
      },
    };
  }, [repo]);
  return useSyncExternalStore(source.subscribe, () => source.get(licenseClass));
}

/** The button's text; "câu N/M" of a chapter never breaks across two lines (375px). */
function continueLabel(action: ContinueAction, licenseClass: string): ReactNode {
  switch (action.kind) {
    case 'start':
      return ht('home.start');
    case 'chapter': {
      const { lead, at } = homeFns.continueChapter(action.chapter, action.position, action.total);
      return (
        <>
          {lead} <span className="home__continue-at">{at}</span>
        </>
      );
    }
    case 'wrong':
      return homeFns.reviewWrong(action.count);
    case 'retake':
      return homeFns.retakeExam(licenseClass);
    case 'exam':
      return homeFns.takeExam(licenseClass);
  }
}

/**
 * The home page (`/`), eager: a control row (class, current regulation set), one "Tiếp tục"
 * button to the most recent unfinished thing, and three equal cards (Lý thuyết, Thi thử, Sa hình
 * 3D). Each card is a link (its primary action is stretched over it) with at most two more links.
 * Numbers come from data: `virtual:theory-counts`, ProgressRepo and the latest saved attempt.
 * Nothing here loads the question bank, the course, the regulation data, `@dcs/theory-core` or
 * `@dcs/api` up front (budgets.json `app.routes["/"]`).
 */
export function HomeScreen(props: HomeScreenProps) {
  const { authStatus, userId, classStore, progressRepo, loadApi, today } = props;
  const { licenseClass, setClass } = useHomeClass(classStore);
  const classCounts = counts[licenseClass];
  const progress = useClassProgress(progressRepo, licenseClass);
  const latest = useLatestAttempt(authStatus === 'authenticated' ? userId : null, loadApi);
  const regulation = currentRegulation(today);
  const slug = slugOf(licenseClass);

  const attempt = latest?.data ?? null;
  const action = continueAction(licenseClass, classCounts, progress, attempt);
  const stats = theoryProgress(progress, classCounts.questions);
  const wrong = progress.wrong.length;

  return (
    <div className="app-page home">
      <h1 className="visually-hidden">{props.title}</h1>
      {props.notice}

      <div className="home__controls">
        <div className="home__class">
          <span id="home-class-label" className="home__class-label">
            {ht('home.classLabel')}
          </span>
          <Segmented
            options={CLASS_OPTIONS}
            value={licenseClass}
            onChange={setClass}
            labelledBy="home-class-label"
            valueAttribute="data-home-class"
          />
        </div>
        <span
          className="home__chip"
          title={REGULATION_FULL[regulation]}
          data-regulation={regulation}
        >
          {homeFns.regulationChip(regulation)}
        </span>
      </div>

      <section className="home__continue" aria-labelledby="home-continue-title">
        <h2 id="home-continue-title" className="visually-hidden">
          {ht('home.continueTitle')}
        </h2>
        <Link
          className="dcs-button dcs-button--primary home__continue-link"
          to={action.to}
          data-continue={action.kind}
        >
          <span className="home__continue-label">{continueLabel(action, licenseClass)}</span>
          <ArrowRightIcon size={20} />
        </Link>
      </section>

      <ul className="home__cards">
        <Card as="li" className="home-card" data-home-card="theory">
          <h2 className="home-card__title">
            <BookIcon size={20} />
            {ht('home.theoryTitle')}
          </h2>
          <div className="home-card__stat">
            <ProgressBar
              value={stats.correct}
              max={stats.total}
              label={homeFns.theoryProgressLabel(licenseClass)}
              valueText={homeFns.theoryProgressValue(stats.correct, stats.total)}
            />
            <p data-theory-stat="">
              {homeFns.theoryStat(stats.correct, stats.total, stats.percent)}
            </p>
          </div>
          <div className="home-card__links">
            <Link className="home-card__link" to={`/ly-thuyet?hang=${slug}&ontap=liet`}>
              {ht('home.criticalLink')}
            </Link>
            {wrong > 0 ? (
              <Link className="home-card__link" to={`/ly-thuyet?hang=${slug}&ontap=sai`}>
                {homeFns.wrongLink(wrong)}
              </Link>
            ) : null}
          </div>
          <CardPrimary to={`/ly-thuyet?hang=${slug}&ontap=chuong`} label={ht('home.theoryLink')} />
        </Card>

        <Card as="li" className="home-card" data-home-card="exam">
          <h2 className="home-card__title">
            <ClipboardIcon size={20} />
            {ht('home.examTitle')}
          </h2>
          <div className="home-card__stat" data-latest-attempt="">
            <LatestAttemptLine authStatus={authStatus} query={latest} attempt={attempt} />
          </div>
          <div className="home-card__links">
            <Link className="home-card__link" to={`/ly-thuyet?hang=${slug}`}>
              {homeFns.examOfClass(licenseClass)}
            </Link>
            <Link className="home-card__link" to="/exam">
              {ht('home.simExam')}
            </Link>
          </div>
          <CardPrimary to="/thi-thu" label={ht('home.examLink')} />
        </Card>

        <Card as="li" className="home-card" data-home-card="practice">
          <h2 className="home-card__title">
            <SteeringWheelIcon size={20} />
            {ht('home.practiceTitle')}
          </h2>
          <p className="home-card__stat">{homeFns.exercisesStat(openExercises)}</p>
          <div className="home-card__links">
            <Link className="home-card__link" to="/sa-hinh">
              {ht('home.exercises')}
            </Link>
          </div>
          <CardPrimary to="/drive" label={ht('home.drive')} />
        </Card>
      </ul>
    </div>
  );
}

/**
 * The card's main action, at its foot: the link is stretched over the whole card (so the card is a
 * link with this name); the other links of the card sit above the stretched area.
 */
function CardPrimary({ to, label }: { readonly to: string; readonly label: string }) {
  return (
    <Link className="home-card__primary" to={to}>
      <span>{label}</span>
      <ArrowRightIcon size={18} />
    </Link>
  );
}

interface LatestAttemptLineProps {
  readonly authStatus: HomeAuthStatus;
  readonly query: LatestAttemptQuery;
  readonly attempt: LatestAttempt | null;
}

/** One line: guest prompt, skeleton while loading, the result, or a one-line notice. */
function LatestAttemptLine({ authStatus, query, attempt }: LatestAttemptLineProps) {
  if (authStatus === 'anonymous') return <p>{ht('home.examGuest')}</p>;
  if (authStatus === 'authenticated' && attempt !== null) {
    return (
      <p>
        {homeFns.latestAttempt(
          attempt.passed,
          attempt.score,
          attempt.maxScore,
          attempt.licenseClass,
        )}
      </p>
    );
  }
  if (authStatus === 'authenticated' && query?.isSuccess === true) {
    return <p>{ht('home.examNone')}</p>;
  }
  if (authStatus === 'authenticated' && query !== null && (query.isError || query.isPaused)) {
    return (
      <p className="home-card__notice" role="status">
        <WarningIcon size={16} />
        {ht(query.isPaused ? 'home.examOffline' : 'home.examError')}
      </p>
    );
  }
  return (
    <p className="home-skeleton" data-skeleton="" aria-busy="true">
      <span className="visually-hidden">{ht('home.examLoading')}</span>
    </p>
  );
}
